<template>
  <div class="guide-authentication-scopes">
    <h2>Authentication and scopes</h2>
    <p>
      Every integration that talks to eSign Launchpad from outside the portal needs to
      authenticate. That includes your own backend, a third-party tool, or an AI agent.
      The platform speaks OAuth 2.0. Each request carries an
      <code>Authorization: Bearer &lt;token&gt;</code> header. Scopes attached to the
      token control what it can do.
    </p>
    <p>
      Scopes let you give a credential exactly the access it needs and nothing more. A
      send-documents service gets <code>sign.package.send</code>. A reporting dashboard
      gets <code>sign.package.read</code>. A template manager gets
      <code>sign.template.create</code>. If a credential is ever leaked, the blast
      radius is limited to what you granted.
    </p>

    <q-banner class="bg-blue-1 q-mb-md" rounded>
      <template v-slot:avatar>
        <q-icon name="info" color="blue-9" />
      </template>
      The full OAuth metadata is published at
      <a href="https://api.esignlaunchpad.com/.well-known/openid-configuration" target="_blank" rel="noopener">
        <code>/.well-known/openid-configuration</code>
      </a>.
      That document is the canonical reference for endpoints, supported grant types,
      and scopes. Standard OAuth/OIDC libraries will discover everything they need
      from it. Public keys for token verification are at
      <a href="https://api.esignlaunchpad.com/.well-known/jwks" target="_blank" rel="noopener">
        <code>/.well-known/jwks</code>
      </a>.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h2>Two credential types</h2>
    <p>
      The portal can mint two kinds of credential. Both flow through the same OAuth
      validation pipeline, both use the same scopes, and both are passed as bearer
      tokens. The difference is how you obtain the token.
    </p>

    <q-markup-table flat bordered dense class="q-mb-md">
      <thead>
        <tr>
          <th class="text-left">Personal Access Token (PAT)</th>
          <th class="text-left">OAuth Client Credentials</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>One long-lived bearer string. Paste it directly into the
              <code>Authorization</code> header. No token endpoint round-trip.</td>
          <td>A <code>client_id</code> + <code>client_secret</code> pair. Exchange
              them at <code>POST /connect/token</code> for a one-hour access token,
              then send that token as a bearer.</td>
        </tr>
        <tr>
          <td>Default 365-day lifetime. Configurable up to 10 years.</td>
          <td>Secret never expires. Access token issued at exchange time lasts one
              hour and is meant to be re-fetched on demand.</td>
        </tr>
        <tr>
          <td>Best for: simple scripts, internal tools, single-machine integrations,
              anyone who values "one string, paste and go."</td>
          <td>Best for: production integrations that want short-lived tokens with
              automatic rotation, anyone using an off-the-shelf OAuth/OIDC library,
              Zapier-style connectors, anything that already speaks OIDC.</td>
        </tr>
        <tr>
          <td>Identifier prefix in portal: <code>pat_</code></td>
          <td>Identifier prefix in portal: <code>eslp_</code></td>
        </tr>
      </tbody>
    </q-markup-table>

    <p>
      Both credential types live under <strong>Settings</strong> then
      <strong>API</strong> in the portal. Pick the one that fits the integration; you
      can mix them inside one tenant.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Generating a credential in the portal</h2>
    <p>
      Credentials are minted through the portal, not via API. Issuing a long-lived
      secret stays behind portal-admin authentication, not automation.
    </p>
    <ol>
      <li>Sign in as a user with the <strong>Tenant Admin</strong> role.</li>
      <li>Open <strong>Settings</strong> then <strong>API</strong> from the sidebar.</li>
      <li>Click <strong>Generate API Key</strong>. Choose <em>Personal Access Token</em>
          or <em>OAuth Client Credentials</em>.</li>
      <li>Name the credential, for example <em>Production Send Service</em>. Pick the
          scopes it needs. Default to the minimum.</li>
      <li>For a PAT, set an expiry (default 365 days). For OAuth, no expiry on the
          secret itself.</li>
      <li>Click <strong>Generate</strong>. The token (PAT) or
          <code>client_secret</code> (OAuth) displays once. Copy it to your secret
          store immediately. It cannot be retrieved later.</li>
    </ol>
    <p>
      API access requires the Business tier or above. Your billing page shows whether
      it is enabled. If not, upgrade the subscription first.
    </p>

    <section class="dev-section">
    <h2>For developers and enterprise integration</h2>
    <p>
      Everything you need to authenticate programmatically is below: the bearer
      header format, the OAuth token exchange, the full scope catalog, and how
      authentication failures surface.
    </p>

    <h3>Personal Access Token: paste and go</h3>
    <p>
      Pass the PAT directly in the <code>Authorization</code> header on every
      request. No token endpoint, no exchange, no refresh logic. The PAT
      <em>is</em> the access token.
    </p>
    <GuideCodeTabs :samples="samples.patRequestExample" />

    <p>
      The token is a signed JWT. If you decode it, you'll see <code>iss</code>,
      <code>sub</code>, <code>scope</code>, <code>tenant_id</code>, <code>exp</code>,
      and <code>jti</code> claims, with <code>typ=at+jwt</code> per
      <a href="https://datatracker.ietf.org/doc/html/rfc9068" target="_blank" rel="noopener">RFC 9068</a>.
      You generally do not need to decode it - treat the whole string as an
      opaque credential.
    </p>

    <q-separator class="q-my-lg" />

    <h3>OAuth Client Credentials: standard token exchange</h3>
    <p>
      With <code>client_id</code> and <code>client_secret</code> in hand, exchange
      them for a one-hour access token at the token endpoint. Send the token as a
      bearer on subsequent API calls. Re-fetch when it expires.
    </p>

    <p class="q-mb-xs"><strong>Step 1.</strong> Exchange credentials for an access token:</p>
    <GuideCodeTabs :samples="samples.tokenExchange" />

    <p class="q-mb-xs"><strong>Step 2.</strong> Use the access token on API calls:</p>
    <GuideCodeTabs :samples="samples.bearerRequest" />

    <p>
      The <code>scope</code> parameter at the token endpoint is optional. Omit it to
      receive a token with all the scopes the client is configured for. Pass a
      space-separated subset to down-scope a particular request.
    </p>
    <p>
      Most OAuth client libraries handle the exchange and caching for you. Point
      them at
      <a href="https://api.esignlaunchpad.com/.well-known/openid-configuration" target="_blank" rel="noopener">
        the discovery document
      </a>
      and they will discover the token endpoint and JWKS automatically.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Authentication failures</h3>
    <p>
      A missing, invalid, or expired token returns <code>401 Unauthorized</code>:
    </p>
    <GuideCodeTabs :samples="samples.unauthorizedResponse" />
    <p>
      A valid token without the right scope returns <code>403 Forbidden</code>:
    </p>
    <GuideCodeTabs :samples="samples.forbiddenResponse" />
    <p>
      Include the <code>correlationId</code> when opening a support ticket so the
      exact request can be traced in our logs.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Scopes</h3>
    <p>
      Scopes are the permissions attached to a credential at creation time. Each
      scope unlocks one or more fine-grained operations. The table below lists every
      scope the API recognises. The complete list is also published in the
      <a href="https://api.esignlaunchpad.com/.well-known/openid-configuration" target="_blank" rel="noopener">discovery document</a>.
    </p>

    <q-markup-table flat bordered dense class="q-mb-md">
      <thead>
        <tr>
          <th class="text-left">Scope</th>
          <th class="text-left">What it grants</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><code>sign.package.read</code></td>
          <td>List and read packages, documents, signers, annotations, audit trails, and download URLs.</td>
        </tr>
        <tr>
          <td><code>sign.package.create</code></td>
          <td>Create packages and upload documents. Add signers, annotations, and template-driven content.</td>
        </tr>
        <tr>
          <td><code>sign.package.update</code></td>
          <td>Update package metadata, modify signers, edit annotations, swap documents on a draft.</td>
        </tr>
        <tr>
          <td><code>sign.package.send</code></td>
          <td>Transition a draft package to sent. This is the moment signers receive their invitation.</td>
        </tr>
        <tr>
          <td><code>sign.package.void</code></td>
          <td>Void a sent package. Signers cannot complete a voided package.</td>
        </tr>
        <tr>
          <td><code>sign.package.delete</code></td>
          <td>Delete drafts. Sent packages are archived, not deleted, to preserve the audit trail.</td>
        </tr>
        <tr>
          <td><code>sign.template.read</code></td>
          <td>List and read templates, their roles, documents, and annotations.</td>
        </tr>
        <tr>
          <td><code>sign.template.create</code></td>
          <td>Create templates from documents. Add roles and annotations.</td>
        </tr>
        <tr>
          <td><code>sign.template.update</code></td>
          <td>Update template metadata, roles, and annotations on existing templates.</td>
        </tr>
        <tr>
          <td><code>sign.template.delete</code></td>
          <td>Delete templates. Packages already created from a template are not affected.</td>
        </tr>
        <tr>
          <td><code>sign.group.read</code></td>
          <td>List and read signing groups and their members.</td>
        </tr>
        <tr>
          <td><code>sign.group.create</code></td>
          <td>Create signing groups and add initial members.</td>
        </tr>
        <tr>
          <td><code>sign.group.update</code></td>
          <td>Add or remove members of an existing signing group.</td>
        </tr>
        <tr>
          <td><code>sign.group.delete</code></td>
          <td>Delete signing groups.</td>
        </tr>
        <tr>
          <td><code>sign.webhook.manage</code></td>
          <td>Create, update, list, and delete webhook subscriptions for the tenant.</td>
        </tr>
      </tbody>
    </q-markup-table>

    <p>
      Creating a package from a template requires both <code>sign.template.read</code>
      and <code>sign.package.create</code>. Sending it requires
      <code>sign.package.send</code>. Most production integrations end up with two or
      three scopes.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Choosing scopes</h3>
    <p>Grant the narrowest set of scopes the integration needs. Common patterns:</p>
    <ul>
      <li>
        <strong>Send-only service.</strong> Creates packages and sends them.
        Needs <code>sign.package.create</code> and <code>sign.package.send</code>.
      </li>
      <li>
        <strong>Reporting dashboard.</strong> Reads status and downloads completed
        documents. Needs <code>sign.package.read</code>.
      </li>
      <li>
        <strong>Template-driven workflow.</strong> Maintains reusable templates in
        code, then creates packages from them. Needs
        <code>sign.template.read</code>, <code>sign.template.update</code>,
        <code>sign.package.create</code>, and <code>sign.package.send</code>.
      </li>
      <li>
        <strong>Webhook installer.</strong> A one-time setup script that registers
        delivery endpoints. Needs <code>sign.webhook.manage</code>.
      </li>
    </ul>

    <q-banner class="bg-amber-1 text-amber-10 q-mb-lg" rounded>
      <template v-slot:avatar>
        <q-icon name="warning" color="amber-10" />
      </template>
      Branding, billing, and tenant settings are portal-only. Configuring the
      company logo, colors, email sender, and signing redirects all require the
      <code>TenantAdmin</code> portal role. No OAuth scope grants those operations
      - intentionally, to keep account-level configuration off the
      OAuth credential surface. Branding lives under <strong>Settings</strong> then
      <strong>Branding</strong> and applies tenant-wide, including to packages
      created via API.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h3>Internal endpoints</h3>
    <p>
      Anything mounted under <code>/api/v1/internal/*</code> - the portal's
      own settings page, key management, billing UI - is not part of the
      public API. Those routes are reachable only from a portal session, never from
      a PAT or OAuth token. Public integrations live under <code>/api/v1/*</code>.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Secure storage</h3>
    <ul>
      <li>Treat the credential like a password. Store it in a secret manager such as Azure Key Vault, AWS Secrets Manager, HashiCorp Vault, or a platform-provided equivalent.</li>
      <li>Never commit credentials to source control. If one lands in a git history, rotate it immediately.</li>
      <li>Never embed credentials in browser-side code. Requests to the eSign Launchpad API must originate from a server-side process.</li>
      <li>Use separate credentials per environment (development, staging, production) so revoking one does not affect the others.</li>
      <li>Up to 25 active credentials per tenant. Revoke unused ones to stay under the cap.</li>
    </ul>

    <q-separator class="q-my-lg" />

    <h3>Rotation</h3>
    <ul>
      <li><strong>PAT:</strong> generate a replacement, cut clients over to it, then revoke the old one. PATs cannot be re-issued with the same string.</li>
      <li><strong>OAuth Client:</strong> generate a fresh client, cut clients over, then revoke the old one. There is no in-place secret rotation; the secret is shown once at generation.</li>
    </ul>

    <q-separator class="q-my-lg" />

    <h3>Related</h3>
    <ul>
      <li><a href="/Docs/IntegrationGuides?topic=quick-start">Quick Start</a>. Send your first package in under five minutes.</li>
      <li><a href="/Docs/IntegrationGuides?topic=errors">Errors &amp; Rate Limits</a>. How 4xx responses are shaped and how the API throttles.</li>
      <li><a href="/Docs/IntegrationGuides?topic=webhooks">Webhooks</a>. Receive events without polling.</li>
    </ul>
    </section>
  </div>
</template>

<script>
import GuideCodeTabs from './shared/GuideCodeTabs.vue';

const samples = {
    patRequestExample: {
        http: `GET /api/v1/packages HTTP/1.1
Host: api.esignlaunchpad.com
Authorization: Bearer pat_a1b2c3d4e5f6...
Accept: application/json`
    },
    tokenExchange: {
        curl: `curl -X POST https://api.esignlaunchpad.com/connect/token \\
  -H "Content-Type: application/x-www-form-urlencoded" \\
  -d "grant_type=client_credentials" \\
  -d "client_id=$ESIGN_CLIENT_ID" \\
  -d "client_secret=$ESIGN_CLIENT_SECRET" \\
  -d "scope=sign.package.read sign.package.create sign.package.send"`,
        javascript: `const tokenResponse = await fetch('https://api.esignlaunchpad.com/connect/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
        grant_type: 'client_credentials',
        client_id: process.env.ESIGN_CLIENT_ID,
        client_secret: process.env.ESIGN_CLIENT_SECRET,
        scope: 'sign.package.read sign.package.create sign.package.send'
    })
});
const { access_token, expires_in } = await tokenResponse.json();
// Cache access_token for (expires_in - 60) seconds, then refresh.`,
        python: `import os, requests

resp = requests.post(
    'https://api.esignlaunchpad.com/connect/token',
    data={
        'grant_type': 'client_credentials',
        'client_id': os.environ['ESIGN_CLIENT_ID'],
        'client_secret': os.environ['ESIGN_CLIENT_SECRET'],
        'scope': 'sign.package.read sign.package.create sign.package.send',
    },
)
resp.raise_for_status()
token = resp.json()['access_token']`
    },
    bearerRequest: {
        curl: `curl https://api.esignlaunchpad.com/api/v1/packages \\
  -H "Authorization: Bearer $ACCESS_TOKEN"`,
        javascript: `const response = await fetch('https://api.esignlaunchpad.com/api/v1/packages', {
    headers: { 'Authorization': \`Bearer \${accessToken}\` }
});`,
        python: `requests.get(
    'https://api.esignlaunchpad.com/api/v1/packages',
    headers={'Authorization': f'Bearer {access_token}'},
)`
    },
    unauthorizedResponse: {
        json: `{
  "errorCode": "UNAUTHORIZED",
  "message": "Bearer token is missing, invalid, or expired.",
  "correlationId": "00-abc123...-01"
}`
    },
    forbiddenResponse: {
        json: `{
  "errorCode": "FORBIDDEN",
  "message": "Token is missing required scope: sign.package.send",
  "correlationId": "00-abc123...-01"
}`
    }
};

export default {
    name: 'GuideAuthenticationScopes',
    components: { GuideCodeTabs },
    data() { return { samples }; }
};
</script>
